import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { useDealContext } from "../../deal/context";
import { CompanyTitle } from "../components/CompanyTitle";
import { NurtureResponses } from "../components/nurture/NurtureResponses"
import { ExecTabs } from "../styles/AdvisorStyles";
import { ExecTab } from "../styles/AdvisorStyles";
import { useEffect, useState } from "react";
import { NurtureAnalytics } from "../components/nurture/NurtureAnalytics";
import { useAuth0 } from "@auth0/auth0-react";
import ProjectHttpClient from "../../../core/infrastructure/clients/v1/ProjectHttpClient";
import { useParams } from "react-router-dom";
import Loading from "../../../components/Loading";
import { ITouchpoint } from "../../../types";
import { DisplayBoxBase } from "../../../components/shared/StyledComponents";
import { InsightResponses } from "../components/insights/InsightResponses";
import { CompanyTouchpoint } from "./CompanyTouchpoint";
import { VideoJS } from "../../../components/VideoJs";
import useAnalytics from "../../../hooks/useAnalytics";
import { useUserContext } from "../../users/context/UserContext";

export const CompanyInsightDetail = () => {

    const { dealContext } = useDealContext();
    const [value, setValue] = useState(0);
    const project = dealContext.project;
    const { touchpointUuid } = useParams();
    const { getAccessTokenSilently } = useAuth0();
    const client = new ProjectHttpClient();
    const [touchpoint, setTouchpoint] = useState<ITouchpoint | null>(null);
    const { trackEvent } = useAnalytics();

    if (!project.insights_enabled) {
        return <CompanyTouchpoint />
    }

    const handleTabSwitch = (event: any, newValue: any) => {
        setValue(newValue);
    }

    useEffect(() => {
        const fetchTouchpointResponses = async () => {
            if (!touchpointUuid) return;
            const accessToken = await getAccessTokenSilently();
            const { data, error } = await client.getTouchpoint(project.id, touchpointUuid, accessToken);
            setTouchpoint(data.touchpoint as ITouchpoint);
            setValue(0);
        }

        if (project.id && touchpointUuid) {
            fetchTouchpointResponses();
        }
    }, [project.id, touchpointUuid]);

    if (!touchpoint) {
        return <Loading />;
    }
    
    const handleVideoPlay = () => {
        trackEvent("Watched highlight video", {
            project: project.tag,
            organization: project.organization.tag,
            touchpoint_uuid: touchpoint.uuid,
            touchpoint_name: touchpoint.name,
            video_url: touchpoint.highlight_video_url,
        });
    }

    return (
        <Box>
            <Typography variant="body1">Insights &gt;</Typography>
            <CompanyTitle viewerRole={dealContext.role} project={project} section={`${touchpoint?.name}`} />

            <Stack direction="column" gap={4}>
                <Box sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 300px",
                    gap: 2,
                    width: "100%",
                    alignItems: "start"
                }}>
                    <Box sx={{
                        aspectRatio: "16/9",
                        width: "100%",
                        height: "auto"
                    }}>
                        {touchpoint.highlight_video_url ? (
                            <VideoJS
                                src={touchpoint.highlight_video_url}
                                startAt={0.5}
                                size="large"
                                onPlay={handleVideoPlay}
                            />
                        ) : (
                            <Box
                                sx={{
                                    width: "100%",
                                    height: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    backgroundColor: "grey.100",
                                    borderRadius: 1,
                                    border: "1px dashed",
                                    borderColor: "grey.300"
                                }}
                            >
                                <Typography variant="h6" color="text.secondary" textAlign="center">
                                    Highlight reel coming soon
                                </Typography>
                            </Box>
                        )}
                    </Box>
                        <DisplayBoxBase className="white column" sx={{
                            width: "300px",
                            height: "fit-content",
                            maxHeight: "100%",
                            overflow: "hidden",
                            display: "flex",
                            flexDirection: "column"
                        }}>
                            <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>Advisors are reacting to...</Typography>
                            { touchpoint.prompt_video_url ? (
                                <VideoJS
                                    src={touchpoint.prompt_video_url}
                                />
                            ) : (
                            <Box sx={{
                                overflow: "hidden",
                                width: "100%",
                                flex: 1,
                                overflowY: "auto",
                                maxHeight: "calc(100vh - 200px)"
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: touchpoint.prompt_html || touchpoint.product_description || ""
                                }} />
                            )}
                        </DisplayBoxBase>
                </Box>

                {touchpoint.media_analysis_summary && (

                <DisplayBoxBase className="gray left" sx={{ width: "100%", height: "100%" }}>
                        <Box dangerouslySetInnerHTML={{
                            __html: touchpoint.media_analysis_summary.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                        }} />
                    </DisplayBoxBase>
                )}

                <Box sx={{ mt: 2 }}>
                    <ExecTabs variant="fullWidth" value={value} onChange={handleTabSwitch} sx={{ mb: 2 }}>
                        <ExecTab label={<span>Individual Responses</span>} sx={{ width: "50%", maxWidth: "50%" }}></ExecTab>
                        <ExecTab label={<span>Summary Analytics</span>} disabled={!touchpoint.analysis_url} sx={{ width: "50%", maxWidth: "50%" }}></ExecTab>
                    </ExecTabs>
                </Box>
                {value === 1 && <NurtureAnalytics project={project} touchpoint={touchpoint} />}
                {value === 0 && <InsightResponses touchpoint={touchpoint} />}
            </Stack>
        </Box>
    )
}